import { categories } from '@/mocks/categories';
import { useRef, useState, useEffect, useCallback } from 'react';

export default function CategoryGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  return (
    <section className="bg-foreground-950 py-16 md:py-24">
      <div className="px-6 md:px-10 mb-10 md:mb-14">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-10">
          <span className="inline-flex px-4 py-1.5 rounded-full border border-background-50/15 text-background-50/70 text-xs font-medium tracking-wide uppercase whitespace-nowrap w-fit font-label">
            Shop Categories
          </span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-background-50 leading-[1.1] tracking-tight">
            Find Your Style
          </h2>
        </div>
      </div>

      <div className="relative px-6 md:px-10">
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 w-[280px] md:w-[320px] group cursor-pointer"
            >
              <div className="bg-background-100/10 rounded-2xl overflow-hidden">
                <div className="w-full h-[280px] md:h-[320px] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`${cat.name} category - Mulebuy Spreadsheet`}
                    title={`${cat.name} - Mulebuy Spreadsheet Shopping Guide`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="mt-4">
                <h3 className="text-base md:text-lg font-semibold text-background-50">{cat.name}</h3>
                <p className="text-sm text-foreground-300/60 mt-1">{cat.itemCount}+ Items</p>
              </div>
            </a>
          ))}
        </div>

        <button
          type="button"
          onClick={() => scroll('left')}
          className={`absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/15 flex items-center justify-center cursor-pointer transition-all duration-200 ${
            canScrollLeft ? 'opacity-100 hover:bg-background-50/20' : 'opacity-0 pointer-events-none'
          }`}
        >
          <i className="ri-arrow-left-s-line text-background-50 text-lg"></i>
        </button>
        <button
          type="button"
          onClick={() => scroll('right')}
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/15 flex items-center justify-center cursor-pointer transition-all duration-200 ${
            canScrollRight ? 'opacity-100 hover:bg-background-50/20' : 'opacity-0 pointer-events-none'
          }`}
        >
          <i className="ri-arrow-right-s-line text-background-50 text-lg"></i>
        </button>
      </div>
    </section>
  );
}